/**
 * CoachPlaybook - Stats Component
 * Version: 0.5.145
 * Description: Player and team statistics tracking with shot charts
 * 
 * Features:
 * - Per-game stat entry
 * - Season totals and averages
 * - Player comparison
 * - Visual shot charts
 */

import React, { useState, useEffect } from 'react';
import JerseyIcon, { DEFAULT_JERSEY_TEMPLATE } from '../common/JerseyIcon';
import ShotChart from './ShotChart';
import { getSetting, setSetting } from '../../utils/db';
import { getAllPlayers } from '../../utils/players';
import { useSport } from '../common/SportContext';

const STATS_KEY = 'playerStats';
const GAMES_KEY = 'games';

// Stat categories
const STAT_CATEGORIES = [
  { key: 'points', label: 'PTS', fullLabel: 'Points', icon: '🏀', color: '#22c55e' },
  { key: 'rebounds', label: 'REB', fullLabel: 'Rebounds', icon: '📊', color: '#3b82f6' },
  { key: 'assists', label: 'AST', fullLabel: 'Assists', icon: '🎯', color: '#8b5cf6' },
  { key: 'steals', label: 'STL', fullLabel: 'Steals', icon: '✋', color: '#f59e0b' },
  { key: 'blocks', label: 'BLK', fullLabel: 'Blocks', icon: '🛡️', color: '#ef4444' },
  { key: 'turnovers', label: 'TO', fullLabel: 'Turnovers', icon: '❌', color: '#dc2626', negative: true },
  { key: 'fouls', label: 'PF', fullLabel: 'Fouls', icon: '🚫', color: '#9ca3af', negative: true },
  { key: 'minutes', label: 'MIN', fullLabel: 'Minutes', icon: '⏱️', color: '#06b6d4' },
  { key: 'fgMade', label: 'FGM', fullLabel: 'FG Made', icon: '✓', color: '#22c55e' },
  { key: 'fgAttempted', label: 'FGA', fullLabel: 'FG Attempted', icon: '○', color: '#64748b' },
  { key: 'threeMade', label: '3PM', fullLabel: '3PT Made', icon: '3️⃣', color: '#22c55e' },
  { key: 'threeAttempted', label: '3PA', fullLabel: '3PT Attempted', icon: '○', color: '#64748b' },
  { key: 'ftMade', label: 'FTM', fullLabel: 'FT Made', icon: '✓', color: '#22c55e' },
  { key: 'ftAttempted', label: 'FTA', fullLabel: 'FT Attempted', icon: '○', color: '#64748b' }
];

// Quick entry stats (most common)
const QUICK_STATS = ['points', 'rebounds', 'assists', 'steals', 'blocks', 'turnovers', 'fouls', 'minutes'];

// Default game
const DEFAULT_GAME = {
  id: '',
  date: new Date().toISOString().split('T')[0],
  opponent: '',
  location: 'home', // home, away, neutral
  result: '', // W, L
  teamScore: '',
  opponentScore: '',
  notes: '',
  playerStats: {}, // { playerId: { points: 0, rebounds: 0, ... } }
  shots: [] // Shot chart data
};

const Stats = ({ onNavigate }) => {
  const { currentSport } = useSport();
  const [players, setPlayers] = useState([]);
  const [games, setGames] = useState([]);
  const [jerseyTemplates, setJerseyTemplates] = useState([]);
  const [activeTemplateId, setActiveTemplateId] = useState(null);
  const [view, setView] = useState('overview'); // overview, games, enterGame, playerDetail, shotChart
  const [selectedGame, setSelectedGame] = useState(null);
  const [editingGame, setEditingGame] = useState(null);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showShotChart, setShowShotChart] = useState(false);
  const [shotChartGame, setShotChartGame] = useState(null);
  
  // Load data - reload when sport changes
  useEffect(() => {
    const loadData = async () => {
      try {
        const loadedPlayers = await getAllPlayers(currentSport);
        const savedGames = await getSetting(GAMES_KEY);
        const templates = await getSetting('jerseyTemplates');
        const activeId = await getSetting('activeJerseyTemplate');
        
        setPlayers(loadedPlayers);
        setGames(savedGames || []);
        setJerseyTemplates(templates || []);
        setActiveTemplateId(activeId);
      } catch (error) {
        console.error('Failed to load stats data:', error);
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, [currentSport]);
  
  const saveGames = async (newGames) => {
    setGames(newGames);
    await setSetting(GAMES_KEY, newGames);
  };
  
  // Open shot chart for a game
  const openShotChart = (game) => {
    setShotChartGame(game);
    setShowShotChart(true);
  };
  
  // Handle shots update from shot chart
  const handleShotsUpdate = async (shots) => {
    if (!shotChartGame) return;
    
    // Update the game with new shots
    const updatedGames = games.map(g => 
      g.id === shotChartGame.id ? { ...g, shots } : g
    );
    
    // Also update FG stats based on shots
    const game2pt = shots.filter(s => s.shotType === '2pt');
    const game3pt = shots.filter(s => s.shotType === '3pt');
    
    // Update player stats with shot chart data
    const updatedWithStats = updatedGames.map(g => {
      if (g.id !== shotChartGame.id) return g;
      
      const playerStats = { ...g.playerStats };
      
      // Calculate per-player shot stats
      players.forEach(p => {
        const playerShots = shots.filter(s => s.playerId === p.id);
        const player2pt = playerShots.filter(s => s.shotType === '2pt');
        const player3pt = playerShots.filter(s => s.shotType === '3pt');
        
        if (playerStats[p.id]) {
          playerStats[p.id] = {
            ...playerStats[p.id],
            fgMade: player2pt.filter(s => s.made).length + player3pt.filter(s => s.made).length,
            fgAttempted: playerShots.length,
            threeMade: player3pt.filter(s => s.made).length,
            threeAttempted: player3pt.length
          };
        }
      });
      
      return { ...g, playerStats, shots };
    });
    
    await saveGames(updatedWithStats);
    setShotChartGame({ ...shotChartGame, shots });
  };
  
  // Get jersey template for player - prefer isDefault
  const getPlayerTemplate = (player) => {
    if (player?.jerseyTemplateId && jerseyTemplates.length > 0) {
      const assigned = jerseyTemplates.find(t => t.id === player.jerseyTemplateId);
      if (assigned) return assigned;
    }
    
    // Prefer default jersey (isDefault), then activeTemplateId, then first
    if (jerseyTemplates.length > 0) {
      const defaultJersey = jerseyTemplates.find(t => t.isDefault);
      if (defaultJersey) return defaultJersey;
      
      if (activeTemplateId) {
        const active = jerseyTemplates.find(t => t.id === activeTemplateId);
        if (active) return active;
      }
      
      return jerseyTemplates[0];
    }
    
    return DEFAULT_JERSEY_TEMPLATE;
  };
  
  // Calculate season stats for a player
  const getPlayerSeasonStats = (playerId) => {
    const playerGames = games.filter(g => g.playerStats && g.playerStats[playerId]);
    
    if (playerGames.length === 0) {
      return { gamesPlayed: 0, totals: {}, averages: {} };
    }
    
    const totals = {};
    STAT_CATEGORIES.forEach(cat => {
      totals[cat.key] = playerGames.reduce((sum, g) => {
        return sum + (parseInt(g.playerStats[playerId]?.[cat.key]) || 0);
      }, 0);
    });
    
    const averages = {};
    STAT_CATEGORIES.forEach(cat => {
      averages[cat.key] = playerGames.length > 0 
        ? (totals[cat.key] / playerGames.length).toFixed(1)
        : '0.0';
    });
    
    // Calculate percentages
    if (totals.fgAttempted > 0) {
      averages.fgPct = ((totals.fgMade / totals.fgAttempted) * 100).toFixed(1);
    }
    if (totals.threeAttempted > 0) {
      averages.threePct = ((totals.threeMade / totals.threeAttempted) * 100).toFixed(1);
    }
    if (totals.ftAttempted > 0) {
      averages.ftPct = ((totals.ftMade / totals.ftAttempted) * 100).toFixed(1);
    }
    
    return { gamesPlayed: playerGames.length, totals, averages };
  };
  
  // Get team totals for a game
  const getGameTeamTotals = (game) => {
    if (!game.playerStats) return {};
    
    const totals = {};
    STAT_CATEGORIES.forEach(cat => {
      totals[cat.key] = Object.values(game.playerStats).reduce((sum, ps) => {
        return sum + (parseInt(ps[cat.key]) || 0);
      }, 0);
    });
    
    return totals;
  };
  
  // Get season leaders
  const getSeasonLeaders = () => {
    const leaders = {};
    
    ['points', 'rebounds', 'assists', 'steals', 'blocks'].forEach(stat => {
      const playerStats = players.map(p => ({
        player: p,
        stats: getPlayerSeasonStats(p.id)
      })).filter(ps => ps.stats.gamesPlayed > 0);
      
      playerStats.sort((a, b) => 
        parseFloat(b.stats.averages[stat] || 0) - parseFloat(a.stats.averages[stat] || 0)
      );
      
      leaders[stat] = playerStats.slice(0, 3);
    });
    
    return leaders;
  };
  
  // Game CRUD
  const handleAddGame = () => {
    setEditingGame({
      ...DEFAULT_GAME,
      id: `game_${Date.now()}`,
      playerStats: players.reduce((acc, p) => {
        acc[p.id] = STAT_CATEGORIES.reduce((stats, cat) => {
          stats[cat.key] = 0;
          return stats;
        }, {});
        return acc;
      }, {})
    });
    setView('enterGame');
  };
  
  const handleEditGame = (game) => {
    // Ensure all players have stats entries
    const playerStats = { ...game.playerStats };
    players.forEach(p => {
      if (!playerStats[p.id]) {
        playerStats[p.id] = STAT_CATEGORIES.reduce((stats, cat) => {
          stats[cat.key] = 0;
          return stats;
        }, {});
      }
    });
    
    setEditingGame({ ...game, playerStats });
    setView('enterGame');
  };
  
  const handleSaveGame = async () => {
    if (!editingGame.opponent.trim()) {
      alert('Please enter opponent name');
      return;
    }
    
    const existing = games.find(g => g.id === editingGame.id);
    const newGames = existing
      ? games.map(g => g.id === editingGame.id ? editingGame : g)
      : [...games, editingGame];
    
    await saveGames(newGames);
    setEditingGame(null);
    setView('games');
  };
  
  const handleDeleteGame = async (gameId) => {
    if (!window.confirm('Delete this game and all stats?')) return;
    const newGames = games.filter(g => g.id !== gameId);
    await saveGames(newGames);
  };
  
  const updatePlayerStat = (playerId, stat, value) => {
    setEditingGame(prev => ({
      ...prev,
      playerStats: {
        ...prev.playerStats,
        [playerId]: {
          ...prev.playerStats[playerId],
          [stat]: Math.max(0, parseInt(value) || 0)
        }
      }
    }));
  };
  
  const incrementStat = (playerId, stat, delta = 1) => {
    const current = editingGame.playerStats[playerId]?.[stat] || 0;
    updatePlayerStat(playerId, stat, current + delta);
  };
  
  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center bg-slate-900">
        <div className="animate-spin w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full" />
      </div>
    );
  }
  
  // ===================== OVERVIEW VIEW =====================
  if (view === 'overview') {
    const leaders = getSeasonLeaders();
    const totalGames = games.length;
    const wins = games.filter(g => g.result === 'W').length;
    const losses = games.filter(g => g.result === 'L').length;
    
    return (
      <div className="h-full flex flex-col bg-slate-900">
        <div className="px-6 py-4 border-b border-slate-700">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-white">📊 Statistics</h1>
              <p className="text-slate-400 text-sm">{totalGames} games played</p>
            </div>
            <button onClick={handleAddGame}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-500">
              + Add Game
            </button>
          </div>
        </div>
        
        <div className="flex-1 overflow-y-auto p-6">
          {players.length === 0 ? (
            <div className="text-center py-16 bg-slate-800 rounded-2xl">
              <div className="text-6xl mb-4">👥</div>
              <h3 className="text-xl font-bold text-white mb-2">No Players Yet</h3>
              <p className="text-slate-400 mb-6">Add players to your roster first</p>
              <button onClick={() => onNavigate('roster')}
                className="px-6 py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-500">
                Go to Roster
              </button>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Season Record */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-slate-800 rounded-xl p-6 text-center">
                  <div className="text-4xl font-black text-white">{totalGames}</div>
                  <div className="text-slate-400 text-sm">Games</div>
                </div>
                <div className="bg-green-900/30 rounded-xl p-6 text-center border border-green-700">
                  <div className="text-4xl font-black text-green-400">{wins}</div>
                  <div className="text-green-300 text-sm">Wins</div>
                </div>
                <div className="bg-red-900/30 rounded-xl p-6 text-center border border-red-700">
                  <div className="text-4xl font-black text-red-400">{losses}</div>
                  <div className="text-red-300 text-sm">Losses</div>
                </div>
              </div>
              
              {/* Quick Actions */}
              <div className="flex gap-3">
                <button onClick={() => setView('games')}
                  className="flex-1 px-4 py-3 bg-slate-800 text-white rounded-xl font-medium hover:bg-slate-700">
                  📅 View All Games
                </button>
                <button onClick={handleAddGame}
                  className="flex-1 px-4 py-3 bg-purple-600 text-white rounded-xl font-medium hover:bg-purple-500">
                  ➕ Enter Game Stats
                </button>
              </div>
              
              {/* Season Leaders */}
              {totalGames > 0 && (
                <div className="bg-slate-800 rounded-xl p-6">
                  <h3 className="text-lg font-bold text-white mb-4">🏆 Season Leaders (Per Game)</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {['points', 'rebounds', 'assists', 'steals', 'blocks'].map(stat => {
                      const statInfo = STAT_CATEGORIES.find(c => c.key === stat);
                      const leader = leaders[stat]?.[0];
                      
                      if (!leader) return null;
                      
                      const template = getPlayerTemplate(leader.player);
                      
                      return (
                        <div key={stat} className="bg-slate-700 rounded-lg p-4">
                          <div className="flex items-center gap-2 mb-3">
                            <span className="text-lg">{statInfo.icon}</span>
                            <span className="text-slate-300 font-medium">{statInfo.fullLabel}</span>
                          </div>
                          <div className="flex items-center gap-3">
                            <JerseyIcon size={40} number={leader.player.jerseyNumber}
                              fillColor={template.fillColor} outlineColor={template.outlineColor}
                              numberColor={template.numberColor} numberOutline={template.numberOutline} />
                            <div className="flex-1">
                              <div className="text-white font-medium">
                                {leader.player.firstName} {leader.player.lastName}
                              </div>
                              <div className="text-slate-400 text-sm">#{leader.player.jerseyNumber}</div>
                            </div>
                            <div className="text-2xl font-black" style={{ color: statInfo.color }}>
                              {leader.stats.averages[stat]}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              
              {/* Player Stats Summary */}
              <div className="bg-slate-800 rounded-xl p-6">
                <h3 className="text-lg font-bold text-white mb-4">📈 Player Averages</h3>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="text-slate-400 text-xs">
                        <th className="text-left py-2 px-2">Player</th>
                        <th className="text-center py-2 px-1">GP</th>
                        {QUICK_STATS.slice(0, 6).map(stat => {
                          const info = STAT_CATEGORIES.find(c => c.key === stat);
                          return <th key={stat} className="text-center py-2 px-1">{info.label}</th>;
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      {players.map(player => {
                        const stats = getPlayerSeasonStats(player.id);
                        const template = getPlayerTemplate(player);
                        
                        return (
                          <tr key={player.id} 
                            className="border-t border-slate-700 hover:bg-slate-700/50 cursor-pointer"
                            onClick={() => { setSelectedPlayer(player); setView('playerDetail'); }}>
                            <td className="py-3 px-2">
                              <div className="flex items-center gap-2">
                                <JerseyIcon size={32} number={player.jerseyNumber}
                                  fillColor={template.fillColor} outlineColor={template.outlineColor}
                                  numberColor={template.numberColor} numberOutline={template.numberOutline} />
                                <div>
                                  <div className="text-white text-sm font-medium">
                                    {player.firstName} {player.lastName?.charAt(0)}.
                                  </div>
                                  <div className="text-slate-500 text-xs">{player.position}</div>
                                </div>
                              </div>
                            </td>
                            <td className="text-center text-slate-300 text-sm">{stats.gamesPlayed}</td>
                            {QUICK_STATS.slice(0, 6).map(stat => (
                              <td key={stat} className="text-center text-white text-sm font-medium">
                                {stats.averages[stat] || '0.0'}
                              </td>
                            ))}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
  
  // ===================== GAMES LIST VIEW =====================
  if (view === 'games') {
    return (
      <div className="h-full flex flex-col bg-slate-900">
        <div className="px-6 py-4 border-b border-slate-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button onClick={() => setView('overview')}
                className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <div>
                <h1 className="text-xl font-bold text-white">📅 Games</h1>
                <p className="text-slate-400 text-sm">{games.length} games recorded</p>
              </div>
            </div>
            <button onClick={handleAddGame}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-500">
              + Add Game
            </button>
          </div>
        </div>
        
        <div className="flex-1 overflow-y-auto p-6">
          {games.length === 0 ? (
            <div className="text-center py-16 bg-slate-800 rounded-2xl">
              <div className="text-6xl mb-4">📅</div>
              <h3 className="text-xl font-bold text-white mb-2">No Games Yet</h3>
              <p className="text-slate-400 mb-6">Add your first game to start tracking stats</p>
              <button onClick={handleAddGame}
                className="px-6 py-3 bg-purple-600 text-white rounded-xl font-semibold hover:bg-purple-500">
                + Add First Game
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {[...games].sort((a, b) => new Date(b.date) - new Date(a.date)).map(game => {
                const totals = getGameTeamTotals(game);
                
                return (
                  <div key={game.id} className="bg-slate-800 rounded-xl p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-xl font-black ${
                          game.result === 'W' ? 'bg-green-600 text-white' : 
                          game.result === 'L' ? 'bg-red-600 text-white' : 
                          'bg-slate-700 text-slate-400'
                        }`}>
                          {game.result || '?'}
                        </div>
                        <div>
                          <div className="text-white font-bold">
                            {game.location === 'away' ? '@ ' : ''}{game.opponent}
                          </div>
                          <div className="text-slate-400 text-sm">
                            {new Date(game.date).toLocaleDateString('en-US', { 
                              weekday: 'short', month: 'short', day: 'numeric' 
                            })}
                          </div>
                          {(game.teamScore || game.opponentScore) && (
                            <div className="text-lg font-bold mt-1">
                              <span className={game.result === 'W' ? 'text-green-400' : 'text-red-400'}>
                                {game.teamScore}
                              </span>
                              <span className="text-slate-500"> - </span>
                              <span className="text-slate-400">{game.opponentScore}</span>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4">
                        <div className="text-right hidden md:block">
                          <div className="text-slate-400 text-xs">Team Stats</div>
                          <div className="text-white text-sm">
                            {totals.points || 0} PTS • {totals.rebounds || 0} REB • {totals.assists || 0} AST
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button onClick={() => openShotChart(game)}
                            className="p-2 bg-green-600/20 text-green-400 rounded-lg hover:bg-green-600/30"
                            title="Shot Chart">
                            🎯
                          </button>
                          <button onClick={() => handleEditGame(game)}
                            className="p-2 bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600">
                            ✏️
                          </button>
                          <button onClick={() => handleDeleteGame(game.id)}
                            className="p-2 bg-red-600/20 text-red-400 rounded-lg hover:bg-red-600/30">
                            🗑️
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  }
  
  // ===================== ENTER GAME STATS VIEW =====================
  if (view === 'enterGame' && editingGame) {
    const teamTotals = getGameTeamTotals(editingGame);
    
    return (
      <div className="h-full flex flex-col bg-slate-900">
        <div className="px-6 py-4 border-b border-slate-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button onClick={() => { setView('games'); setEditingGame(null); }}
                className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <h1 className="text-xl font-bold text-white">
                {games.find(g => g.id === editingGame.id) ? 'Edit Game' : 'New Game'}
              </h1>
            </div>
            <div className="flex gap-2">
              <button onClick={() => openShotChart(editingGame)}
                className="px-4 py-2 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-500 flex items-center gap-2">
                🎯 Shot Chart
              </button>
              <button onClick={() => { setView('games'); setEditingGame(null); }}
                className="px-4 py-2 bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600">
                Cancel
              </button>
              <button onClick={handleSaveGame}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-500">
                💾 Save Game
              </button>
            </div>
          </div>
        </div>
        
        <div className="flex-1 overflow-y-auto">
          {/* Game Info */}
          <div className="px-6 py-4 bg-slate-800 border-b border-slate-700">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-xs text-slate-400 mb-1">Date</label>
                <input type="date" value={editingGame.date}
                  onChange={(e) => setEditingGame(prev => ({ ...prev, date: e.target.value }))}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm" />
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1">Opponent *</label>
                <input type="text" value={editingGame.opponent}
                  onChange={(e) => setEditingGame(prev => ({ ...prev, opponent: e.target.value }))}
                  placeholder="Team name"
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm" />
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1">Location</label>
                <select value={editingGame.location}
                  onChange={(e) => setEditingGame(prev => ({ ...prev, location: e.target.value }))}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm">
                  <option value="home">Home</option>
                  <option value="away">Away</option>
                  <option value="neutral">Neutral</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1">Result</label>
                <div className="flex gap-2">
                  <button type="button"
                    onClick={() => setEditingGame(prev => ({ ...prev, result: 'W' }))}
                    className={`flex-1 px-3 py-2 rounded-lg font-bold ${
                      editingGame.result === 'W' ? 'bg-green-600 text-white' : 'bg-slate-700 text-slate-400'
                    }`}>W</button>
                  <button type="button"
                    onClick={() => setEditingGame(prev => ({ ...prev, result: 'L' }))}
                    className={`flex-1 px-3 py-2 rounded-lg font-bold ${
                      editingGame.result === 'L' ? 'bg-red-600 text-white' : 'bg-slate-700 text-slate-400'
                    }`}>L</button>
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4 mt-4">
              <div>
                <label className="block text-xs text-slate-400 mb-1">Our Score</label>
                <input type="number" min="0" value={editingGame.teamScore}
                  onChange={(e) => setEditingGame(prev => ({ ...prev, teamScore: e.target.value }))}
                  placeholder="0"
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm" />
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1">Their Score</label>
                <input type="number" min="0" value={editingGame.opponentScore}
                  onChange={(e) => setEditingGame(prev => ({ ...prev, opponentScore: e.target.value }))}
                  placeholder="0"
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm" />
              </div>
            </div>
            
            {/* Team Totals Summary */}
            <div className="flex items-center gap-4 mt-4 p-3 bg-slate-700 rounded-lg">
              <span className="text-slate-400 text-sm">Team Totals:</span>
              <span className="text-white font-bold">{teamTotals.points || 0} PTS</span>
              <span className="text-white font-bold">{teamTotals.rebounds || 0} REB</span>
              <span className="text-white font-bold">{teamTotals.assists || 0} AST</span>
            </div>
          </div>
          
          {/* Player Stats Entry */}
          <div className="p-6">
            <h3 className="text-lg font-bold text-white mb-4">Player Stats</h3>
            <div className="space-y-4">
              {players.map(player => {
                const template = getPlayerTemplate(player);
                const playerStats = editingGame.playerStats[player.id] || {};
                
                return (
                  <div key={player.id} className="bg-slate-800 rounded-xl p-4">
                    <div className="flex items-center gap-3 mb-4">
                      <JerseyIcon size={40} number={player.jerseyNumber}
                        fillColor={template.fillColor} outlineColor={template.outlineColor}
                        numberColor={template.numberColor} numberOutline={template.numberOutline} />
                      <div>
                        <div className="text-white font-medium">
                          {player.firstName} {player.lastName}
                        </div>
                        <div className="text-slate-400 text-xs">#{player.jerseyNumber} • {player.position}</div>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-4 md:grid-cols-8 gap-2">
                      {QUICK_STATS.map(stat => {
                        const info = STAT_CATEGORIES.find(c => c.key === stat);
                        const value = playerStats[stat] || 0;
                        
                        return (
                          <div key={stat} className="text-center">
                            <label className="block text-xs text-slate-400 mb-1">{info.label}</label>
                            <div className="flex items-center justify-center gap-1">
                              <button type="button"
                                onClick={() => incrementStat(player.id, stat, -1)}
                                className="w-6 h-6 bg-slate-700 text-slate-400 rounded text-xs hover:bg-slate-600">
                                -
                              </button>
                              <input type="number" min="0" value={value}
                                onChange={(e) => updatePlayerStat(player.id, stat, e.target.value)}
                                className="w-10 px-1 py-1 bg-slate-700 border border-slate-600 rounded text-white text-sm text-center" />
                              <button type="button"
                                onClick={() => incrementStat(player.id, stat, 1)}
                                className="w-6 h-6 bg-slate-700 text-slate-400 rounded text-xs hover:bg-slate-600">
                                +
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  // ===================== PLAYER DETAIL VIEW =====================
  if (view === 'playerDetail' && selectedPlayer) {
    const stats = getPlayerSeasonStats(selectedPlayer.id);
    const template = getPlayerTemplate(selectedPlayer);
    const playerGames = games.filter(g => g.playerStats && g.playerStats[selectedPlayer.id]);
    
    return (
      <div className="h-full flex flex-col bg-slate-900">
        <div className="px-6 py-4 border-b border-slate-700">
          <div className="flex items-center gap-4">
            <button onClick={() => { setView('overview'); setSelectedPlayer(null); }}
              className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <JerseyIcon size={50} number={selectedPlayer.jerseyNumber}
              fillColor={template.fillColor} outlineColor={template.outlineColor}
              numberColor={template.numberColor} numberOutline={template.numberOutline} />
            <div>
              <h1 className="text-xl font-bold text-white">
                {selectedPlayer.firstName} {selectedPlayer.lastName}
              </h1>
              <p className="text-slate-400 text-sm">#{selectedPlayer.jerseyNumber} • {selectedPlayer.position}</p>
            </div>
          </div>
        </div>
        
        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Season Averages */}
            <div className="bg-slate-800 rounded-xl p-6">
              <h3 className="text-lg font-bold text-white mb-4">Season Averages</h3>
              <div className="text-center mb-4">
                <div className="text-sm text-slate-400">{stats.gamesPlayed} Games Played</div>
              </div>
              <div className="grid grid-cols-4 gap-4">
                {QUICK_STATS.slice(0, 4).map(stat => {
                  const info = STAT_CATEGORIES.find(c => c.key === stat);
                  return (
                    <div key={stat} className="text-center">
                      <div className="text-3xl font-black" style={{ color: info.color }}>
                        {stats.averages[stat] || '0.0'}
                      </div>
                      <div className="text-xs text-slate-400">{info.label}</div>
                    </div>
                  );
                })}
              </div>
              <div className="grid grid-cols-4 gap-4 mt-4">
                {QUICK_STATS.slice(4, 8).map(stat => {
                  const info = STAT_CATEGORIES.find(c => c.key === stat);
                  return (
                    <div key={stat} className="text-center">
                      <div className="text-2xl font-bold text-white">
                        {stats.averages[stat] || '0.0'}
                      </div>
                      <div className="text-xs text-slate-400">{info.label}</div>
                    </div>
                  );
                })}
              </div>
              
              {/* Shooting Percentages */}
              {stats.gamesPlayed > 0 && (
                <div className="grid grid-cols-3 gap-4 mt-6 pt-4 border-t border-slate-700">
                  <div className="text-center">
                    <div className="text-xl font-bold text-white">{stats.averages.fgPct || '-'}%</div>
                    <div className="text-xs text-slate-400">FG%</div>
                  </div>
                  <div className="text-center">
                    <div className="text-xl font-bold text-white">{stats.averages.threePct || '-'}%</div>
                    <div className="text-xs text-slate-400">3P%</div>
                  </div>
                  <div className="text-center">
                    <div className="text-xl font-bold text-white">{stats.averages.ftPct || '-'}%</div>
                    <div className="text-xs text-slate-400">FT%</div>
                  </div>
                </div>
              )}
            </div>
            
            {/* Season Totals */}
            <div className="bg-slate-800 rounded-xl p-6">
              <h3 className="text-lg font-bold text-white mb-4">Season Totals</h3>
              <div className="grid grid-cols-2 gap-3">
                {QUICK_STATS.map(stat => {
                  const info = STAT_CATEGORIES.find(c => c.key === stat);
                  return (
                    <div key={stat} className="flex items-center justify-between p-3 bg-slate-700 rounded-lg">
                      <div className="flex items-center gap-2">
                        <span>{info.icon}</span>
                        <span className="text-slate-300 text-sm">{info.fullLabel}</span>
                      </div>
                      <span className="text-white font-bold">{stats.totals[stat] || 0}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          
          {/* Game Log */}
          <div className="bg-slate-800 rounded-xl p-6 mt-6">
            <h3 className="text-lg font-bold text-white mb-4">Game Log</h3>
            {playerGames.length === 0 ? (
              <div className="text-center py-8 text-slate-400">No games recorded</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-slate-400 text-xs border-b border-slate-700">
                      <th className="text-left py-2 px-2">Date</th>
                      <th className="text-left py-2 px-2">Opponent</th>
                      {QUICK_STATS.slice(0, 6).map(stat => {
                        const info = STAT_CATEGORIES.find(c => c.key === stat);
                        return <th key={stat} className="text-center py-2 px-1">{info.label}</th>;
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {[...playerGames].sort((a, b) => new Date(b.date) - new Date(a.date)).map(game => {
                      const gs = game.playerStats[selectedPlayer.id] || {};
                      return (
                        <tr key={game.id} className="border-b border-slate-700/50">
                          <td className="py-2 px-2 text-slate-400">
                            {new Date(game.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </td>
                          <td className="py-2 px-2 text-white">
                            {game.location === 'away' ? '@ ' : ''}{game.opponent}
                          </td>
                          {QUICK_STATS.slice(0, 6).map(stat => (
                            <td key={stat} className="text-center py-2 px-1 text-white">
                              {gs[stat] || 0}
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
  
  // Shot Chart View
  if (showShotChart && shotChartGame) {
    return (
      <ShotChart
        game={shotChartGame}
        players={players}
        jerseyTemplates={jerseyTemplates}
        activeTemplateId={activeTemplateId}
        onShotsUpdate={handleShotsUpdate}
        onClose={() => {
          setShowShotChart(false);
          setShotChartGame(null);
        }}
      />
    );
  }
  
  return null;
};

export default Stats;
